import classNames from "classnames";
import React from "react";
import dayjs from "~/lib/dayjs";
import { KIND } from "./relitixDetailsConfig";

const currencyFormatter = Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatTeamLeader = (value) => {
  if (!value) {
    return "";
  }

  const trimmed = value.trim();
  const match = trimmed.match(/^(.+?)\s+\d+-(\d+)$/);

  if (match) {
    const [, name, agentId] = match;
    return `${name.trim()} (${agentId})`;
  }

  return trimmed;
};

const normalizedPercent = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const number = Number(value);
  if (!Number.isFinite(number)) {
    return null;
  }

  return number <= 1 ? number * 100 : number;
};

const badge = (children, className) => (
  <span className={classNames("badge", className)}>{children}</span>
);

const switchRiskBadge = (value) => {
  if (!value) {
    return "";
  }

  const normalized = String(value).toLowerCase();
  const className = {
    green: "badge-success",
    yellow: "badge-warning",
    red: "badge-danger",
  }[normalized];

  return className ? badge(value, className) : value;
};

const rookieRatingBadge = (value) => {
  const percent = normalizedPercent(value);
  if (percent === null) {
    return "";
  }

  if (percent < 35) {
    return badge(`${value} Low potential`, "badge-danger");
  }
  if (percent < 70) {
    return badge(`${value} Moderate potential`, "badge-warning");
  }
  return badge(`${value} High potential`, "badge-success");
};

const gradeBadge = (value) => {
  if (!value) {
    return "";
  }

  const grade = String(value).trim().toUpperCase();
  let className = "badge-secondary";
  if (grade.startsWith("A")) {
    className = "badge-success";
  } else if (grade.startsWith("B") || grade.startsWith("C")) {
    className = "badge-warning";
  } else if (grade.startsWith("D") || grade.startsWith("F")) {
    className = "badge-danger";
  }

  return badge(value, className);
};

const listingEffectivenessImpact = (value) => {
  if (!value) {
    return "";
  }

  const formatted = currencyFormatter.format(value);
  const number = Number(value);

  if (number < 0) {
    return <span className="text-danger">{formatted}</span>;
  }
  if (number > 5000000) {
    return <span className="text-success">{formatted}</span>;
  }
  return formatted;
};

const battingAverageBadge = (value) => {
  const percent = normalizedPercent(value);
  if (percent === null) {
    return "";
  }

  const label = `${Math.round(percent)}%`;
  if (percent < 50) {
    return badge(`${label} Low`, "badge-danger");
  }
  if (percent <= 75) {
    return badge(`${label} Average`, "badge-warning");
  }
  return badge(`${label} Strong`, "badge-success");
};

export const renderValue = (value, kind) => {
  switch (kind) {
    case KIND.currency:
      return value ? currencyFormatter.format(value) : "";
    case KIND.date:
      return value ? dayjs(value).format("L") : "";
    case KIND.datetime:
      return value ? dayjs(value).format("MM/DD/YYYY h:mm A") : "";
    case KIND.distance:
      return value ? `${value} mi` : "";
    case KIND.listingEffectivenessGrade:
      return gradeBadge(value);
    case KIND.listingEffectivenessImpact:
      return listingEffectivenessImpact(value);
    case KIND.number:
      return value ?? "";
    case KIND.percent:
      return battingAverageBadge(value);
    case KIND.rookieRating:
      return rookieRatingBadge(value);
    case KIND.switchRisk:
      return switchRiskBadge(value);
    case KIND.teamLeader:
      return formatTeamLeader(value);
    case KIND.text:
    default:
      return value ?? "";
  }
};

export { formatTeamLeader };
